import React, { memo, Fragment, type ReactNode } from 'react';
import { SmartCardProvider } from '@atlaskit/link-provider';
import client from '@atlassian/jira-apollo-gira';
import { ApolloClientProvider } from '@atlassian/jira-apollo-multiple-clients';
import { BusinessAppWrapper } from '@atlassian/jira-business-app-wrapper';
import {
	useTenantHasConfluence,
	ConfluenceModalProvider,
} from '@atlassian/jira-business-confluence-pages';
import {
	IssueTypesAndFieldsContext,
	RefetchFieldsController,
} from '@atlassian/jira-business-entity-project';
import { useProject } from '@atlassian/jira-business-entity-project-hook';
import { ExperienceErrorBoundary } from '@atlassian/jira-business-experience-tracking';
import { isJwmInlineFieldCreateEnabled } from '@atlassian/jira-business-feature-flags';
import { IssueCreateProvider } from '@atlassian/jira-business-issue-create';
import { WorkflowsProvider } from '@atlassian/jira-business-workflows';
import LoginRedirect from '@atlassian/jira-common-navigation/src/login-redirect';
import { AuthenticationErrorBoundary } from '@atlassian/jira-error-boundaries';
import { ff } from '@atlassian/jira-feature-flagging';
import { fg } from '@atlassian/jira-feature-gating';
import { STATUS_TYPE } from '@atlassian/jira-platform-field-config';
import {
	ContextualAnalyticsData,
	FireScreenAnalytics,
	SCREEN,
} from '@atlassian/jira-product-analytics-bridge';
import { VIEW_EXPERIENCE } from './common/constants';
import { useJWMListFeatures } from './controllers/features-context';
import { useGroupBy } from './controllers/group-by';
import { CREATE_ISSUE_MUTATION, ISSUE_REFETCH_QUERY } from './services/create-issue';
import ListView from './ui/list-view';

const analyticsSourceName = 'listView';

type ListViewProps = {
	header?: ReactNode;
};

export const ListViewRoot = memo<ListViewProps>(({ header }) => {
	const { isSoftwareFieldsEnabled } = useJWMListFeatures();
	const projectData = useProject();
	const projectId = String(projectData.id);
	const projectKey = projectData.key;
	const projectName = projectData.name;
	const isTeamManaged = projectData.isSimplified;
	const [groupByFieldId] = useGroupBy();
	const isGroupingByStatus = groupByFieldId === STATUS_TYPE;

	const ConditionalWorkflowProvider =
		fg('jwm_conditional_workflow_data_loading') || isGroupingByStatus
			? WorkflowsProvider
			: Fragment;

	const content = (
		<ContextualAnalyticsData objectType="list" sourceType={SCREEN} sourceName={analyticsSourceName}>
			<ListView
				projectKey={projectKey}
				projectName={projectName}
				projectId={projectId}
				isTeamManaged={isTeamManaged}
			/>
			<FireScreenAnalytics attributes={{ projectKey }} />
		</ContextualAnalyticsData>
	);

	return (
		<BusinessAppWrapper
			header={header}
			sidebarTheme={
				ff('jsw.list.sidebar-layout-update') && isSoftwareFieldsEnabled ? 'software' : 'business'
			}
			withSidebar
			withOverflow
		>
			<ApolloClientProvider client={client}>
				<IssueTypesAndFieldsContext>
					<ConditionalWorkflowProvider enabled={isGroupingByStatus}>
						<IssueCreateProvider mutation={CREATE_ISSUE_MUTATION} query={ISSUE_REFETCH_QUERY}>
							{content}
						</IssueCreateProvider>
					</ConditionalWorkflowProvider>
					{isJwmInlineFieldCreateEnabled() && <RefetchFieldsController />}
				</IssueTypesAndFieldsContext>
			</ApolloClientProvider>
		</BusinessAppWrapper>
	);
});

export const ListViewRootWithSmartCard = (props: ListViewProps) => {
	const hasConfluence = useTenantHasConfluence();

	if (hasConfluence) {
		return (
			<SmartCardProvider>
				<ConfluenceModalProvider>
					<ListViewRoot {...props} />
				</ConfluenceModalProvider>
			</SmartCardProvider>
		);
	}
	return <ListViewRoot {...props} />;
};

const List = (props: ListViewProps) => (
	<ExperienceErrorBoundary experience={VIEW_EXPERIENCE} shouldStartExperience>
		{fg('jira_list_add_401_error_handling') ? (
			<AuthenticationErrorBoundary render={() => <LoginRedirect />}>
				<ListViewRootWithSmartCard {...props} />
			</AuthenticationErrorBoundary>
		) : (
			<ListViewRootWithSmartCard {...props} />
		)}
	</ExperienceErrorBoundary>
);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export default memo<Record<any, any>>(List);
